import React, { useState, useCallback, useEffect } from 'react';
import KPICard from '../../components/Dashboard/KPICard/KPICard';
import LineChart from '../../components/Dashboard/LineChart/LineChart';
import StackedBarChart from '../../components/Dashboard/StackedBarChart/StackedBarChart';
import BarChart from '../../components/Dashboard/BarChart/BarChart';
import PieChart from '../../components/Dashboard/PieChart/PieChart';
import FunnelChart from '../../components/Dashboard/FunnelChart/FunnelChart';
import GroupedBarChart from '../../components/Dashboard/GroupedBarChart/GroupedBarChart';
import { fetchApi } from '../../../helpers/fetch';
import './Admin.scss';

const Creators = () => {
    const [dateRange, setDateRange] = useState('7days');
    const [loading, setLoading] = useState(false);
    
    // Dummy data - will be replaced with API call later
    const [creatorsData, setCreatorsData] = useState({
        // Creators Section KPIs
        totalCreations: 132,
        arpc: 2000, // Average Revenue Per Creator in cents
        avgGrnvPerCreator: 19050, // in cents
        creationsOnUsersRate: 22, // Also check creatorsOnUsersRate from API
        creatorsOnUsersRate: 22, // Alternative field name from API
        avgTimeToFirstCreation: 1, // days
        creationFrequency: 4, // days
        avgTimeToFirstEarning: 10, // days
        offerVsDemand: 33.5,
        
        // Creators Charts
        creatorsPaidVsFree: [
            { date: '10/11', 'Paid': 55, 'Free': 45 },
            { date: '11/11', 'Paid': 57, 'Free': 43 },
            { date: '12/11', 'Paid': 58, 'Free': 42 },
            { date: '13/11', 'Paid': 59, 'Free': 41 },
            { date: '14/11', 'Paid': 58, 'Free': 42 },
            { date: '15/11', 'Paid': 57, 'Free': 43 },
            { date: '16/11', 'Paid': 59, 'Free': 41 }
        ],
        creatorsPaidVsFreePercentage: 63,
        creatorsPerTier: [
            { name: 'Super A', value: 40 },
            { name: 'A', value: 30 },
            { name: 'B', value: 28 },
            { name: 'C', value: 2 }
        ],
        
        // Conversion and Activation
        conversionPerTier: [
            { tier: 'Super A', 'Sign-ups': 120, 'Profiles completed': 95, 'First upload': 45, 'First sales': 12 },
            { tier: 'A', 'Sign-ups': 150, 'Profiles completed': 120, 'First upload': 65, 'First sales': 18 },
            { tier: 'B', 'Sign-ups': 180, 'Profiles completed': 145, 'First upload': 85, 'First sales': 25 },
            { tier: 'C', 'Sign-ups': 100, 'Profiles completed': 75, 'First upload': 35, 'First sales': 8 }
        ],
        totalSalesSuperA: 390,
        totalSalesA: 320,
        totalSalesB: 280,
        totalSalesC: 190,
        conversionFunnel: [
            { name: 'Views', value: 67580 },
            { name: 'Sign-ups', value: 21190 },
            { name: 'Profiles Completed', value: 16115 },
            { name: 'First upload', value: 4079 },
            { name: 'First sale', value: 533 }
        ],
        
        // Creations and News
        avgCreationsPerCreator: 3,
        newCreations: 54,
        news: 17,
        totalNews: 872,
        avgNewsPerCreator: 19,
        creationsPerCulturalArea: [
            { name: 'Music', value: 45 },
            { name: 'Arts', value: 32 },
            { name: 'Comedy', value: 28 },
            { name: 'Gaming', value: 25 },
            { name: 'Drama', value: 22 },
            { name: 'Lifestyle', value: 20 },
            { name: 'Design', value: 18 },
            { name: 'Literature', value: 15 },
            { name: 'Dance', value: 12 },
            { name: 'Sports', value: 10 },
            { name: 'Opportunities', value: 48 }
        ],
        creationsPerCategory: [
            { name: 'Category 1', value: 45 },
            { name: 'Category 2', value: 32 },
            { name: 'Category 3', value: 28 },
            { name: 'Category 4', value: 25 },
            { name: 'Category 5', value: 22 }
        ],
        paidVsFreeCreations: [
            { name: 'Paid Creations', value: 54 },
            { name: 'Free Creations', value: 46 }
        ],
        paidCreations: 77,
        newCreatedOffers: 24,
        newCreationsAndNewsTrend: [
            { date: '10/11', 'New Creations': 8, 'News': 2 },
            { date: '11/11', 'New Creations': 9, 'News': 3 },
            { date: '12/11', 'New Creations': 7, 'News': 2 },
            { date: '13/11', 'New Creations': 8, 'News': 3 },
            { date: '14/11', 'New Creations': 9, 'News': 2 },
            { date: '15/11', 'New Creations': 7, 'News': 3 },
            { date: '16/11', 'New Creations': 6, 'News': 1 }
        ],
        offerVsDemandTrend: [
            { date: '10/11', 'Paid creations': 10, 'New created offers': 4 },
            { date: '11/11', 'Paid creations': 12, 'New created offers': 3 },
            { date: '12/11', 'Paid creations': 11, 'New created offers': 4 },
            { date: '13/11', 'Paid creations': 13, 'New created offers': 3 },
            { date: '14/11', 'Paid creations': 11, 'New created offers': 4 },
            { date: '15/11', 'Paid creations': 12, 'New created offers': 3 },
            { date: '16/11', 'Paid creations': 8, 'New created offers': 3 }
        ]
    });

    const formatCurrency = useCallback((amount) => {
        return `€${(amount / 100).toFixed(2)}`;
    }, []);

    const formatPercentage = useCallback((value) => {
        return `${value}%`;
    }, []);

    // Helper function to extract trend data array
    // Handles: array of numbers, array of objects with {date, value}, or array of objects with just {value}
    const extractTrendData = useCallback((trendArray) => {
        if (!trendArray || !Array.isArray(trendArray) || trendArray.length === 0) {
            return undefined;
        }
        
        const trendData = trendArray.map(item => {
            if (typeof item === 'number') {
                return item;
            }
            if (item && typeof item === 'object') {
                // Handle {date: "...", value: 123} or {value: 123}
                return item.value !== undefined ? item.value : item;
            }
            return Number(item) || 0;
        }).filter(val => !isNaN(val) && val !== null && val !== undefined);
        
        return trendData.length > 0 ? trendData : undefined;
    }, []);

    // Helper function to extract value and trend from API response
    // Handles both formats: { value, trend, trendLabel, absoluteChange, trendData } or simple value
    const extractValueAndTrend = useCallback((data) => {
        if (data && typeof data === 'object' && 'value' in data) {
            // New format with trend data
            const trendValue = data.trend ? parseFloat(data.trend.replace(/[+%]/g, '')) : null;
            return {
                value: data.value,
                trend: trendValue,
                absoluteChange: data.absoluteChange !== undefined ? data.absoluteChange : null,
                trendLabel: data.trendLabel || 'since last week',
                trendData: data.trendData || null
            };
        }
        // Old format or simple value
        return {
            value: data || 0,
            trend: null,
            absoluteChange: null,
            trendLabel: 'since last week',
            trendData: null
        };
    }, []);

    // Dummy data for Conversion section (not implemented yet) - defined outside callback
    const conversionDummyData = {
        conversionPerTier: [
            { tier: 'Super A', 'Sign-ups': 120, 'Profiles completed': 95, 'First upload': 45, 'First sales': 12 },
            { tier: 'A', 'Sign-ups': 150, 'Profiles completed': 120, 'First upload': 65, 'First sales': 18 },
            { tier: 'B', 'Sign-ups': 180, 'Profiles completed': 145, 'First upload': 85, 'First sales': 25 },
            { tier: 'C', 'Sign-ups': 100, 'Profiles completed': 75, 'First upload': 35, 'First sales': 8 }
        ],
        totalSalesSuperA: 390,
        totalSalesA: 320,
        totalSalesB: 280,
        totalSalesC: 190,
        conversionFunnel: [
            { name: 'Views', value: 67580 },
            { name: 'Sign-ups', value: 21190 },
            { name: 'Profiles Completed', value: 16115 },
            { name: 'First upload', value: 4079 },
            { name: 'First sale', value: 533 }
        ]
    };

    const fetchCreatorsData = useCallback(async () => {
        try {
            setLoading(true);
            
            const response = await fetchApi(`/admin/creators?period=${dateRange}`);
            
            console.log('/admin/creators response:', response);
            
            if (response.success && response.data) {
                console.log('Creators data keys:', Object.keys(response.data));
                console.log('creationsOnUsersRate:', response.data.creationsOnUsersRate);
                console.log('creatorsOnUsersRate:', response.data.creatorsOnUsersRate);
                
                // Merge API data with conversion dummy data
                setCreatorsData({
                    ...response.data,
                    // Handle potential field name mismatch - check both names
                    creationsOnUsersRate: response.data.creationsOnUsersRate !== undefined 
                        ? response.data.creationsOnUsersRate 
                        : (response.data.creatorsOnUsersRate !== undefined 
                            ? response.data.creatorsOnUsersRate 
                            : creatorsData.creationsOnUsersRate),
                    // Keep conversion data as dummy (not from API)
                    conversionPerTier: conversionDummyData.conversionPerTier,
                    totalSalesSuperA: conversionDummyData.totalSalesSuperA,
                    totalSalesA: conversionDummyData.totalSalesA,
                    totalSalesB: conversionDummyData.totalSalesB,
                    totalSalesC: conversionDummyData.totalSalesC,
                    conversionFunnel: conversionDummyData.conversionFunnel
                });
            } else {
                console.warn('API response not successful:', response);
                // If API fails, reset to empty/zero values but keep conversion dummy data
                setCreatorsData({
                    totalCreations: 0,
                    arpc: 0,
                    avgGrnvPerCreator: 0,
                    creationsOnUsersRate: 0,
                    avgTimeToFirstCreation: 0,
                    creationFrequency: 0,
                    avgTimeToFirstEarning: 0,
                    offerVsDemand: 0,
                    creatorsPaidVsFree: [],
                    creatorsPaidVsFreePercentage: 0,
                    creatorsPerTier: [],
                    avgCreationsPerCreator: 0,
                    newCreations: 0,
                    news: 0,
                    totalNews: 0,
                    avgNewsPerCreator: 0,
                    creationsPerCulturalArea: [],
                    creationsPerCategory: [],
                    paidVsFreeCreations: [],
                    paidCreations: 0,
                    newCreatedOffers: 0,
                    newCreationsAndNewsTrend: [],
                    offerVsDemandTrend: [],
                    // Keep conversion data as dummy
                    conversionPerTier: conversionDummyData.conversionPerTier,
                    totalSalesSuperA: conversionDummyData.totalSalesSuperA,
                    totalSalesA: conversionDummyData.totalSalesA,
                    totalSalesB: conversionDummyData.totalSalesB,
                    totalSalesC: conversionDummyData.totalSalesC,
                    conversionFunnel: conversionDummyData.conversionFunnel
                });
            }
        } catch (error) {
            console.error('Error fetching creators data:', error);
            // On error, reset to empty/zero values but keep conversion dummy data
            setCreatorsData({
                totalCreations: 0,
                arpc: 0,
                avgGrnvPerCreator: 0,
                creationsOnUsersRate: 0,
                avgTimeToFirstCreation: 0,
                creationFrequency: 0,
                avgTimeToFirstEarning: 0,
                offerVsDemand: 0,
                creatorsPaidVsFree: [],
                creatorsPaidVsFreePercentage: 0,
                creatorsPerTier: [],
                avgCreationsPerCreator: 0,
                newCreations: 0,
                news: 0,
                totalNews: 0,
                avgNewsPerCreator: 0,
                creationsPerCulturalArea: [],
                creationsPerCategory: [],
                paidVsFreeCreations: [],
                paidCreations: 0,
                newCreatedOffers: 0,
                newCreationsAndNewsTrend: [],
                offerVsDemandTrend: [],
                // Keep conversion data as dummy
                conversionPerTier: conversionDummyData.conversionPerTier,
                totalSalesSuperA: conversionDummyData.totalSalesSuperA,
                totalSalesA: conversionDummyData.totalSalesA,
                totalSalesB: conversionDummyData.totalSalesB,
                totalSalesC: conversionDummyData.totalSalesC,
                conversionFunnel: conversionDummyData.conversionFunnel
            });
        } finally {
            setLoading(false);
        }
    }, [dateRange]);

    useEffect(() => {
        fetchCreatorsData();
    }, [fetchCreatorsData]);

    return (
        <div className="creators-dashboard">
            <div className="dashboard-header">
                <div className="header-controls">
                    <select 
                        value={dateRange} 
                        onChange={(e) => setDateRange(e.target.value)}
                        className="date-range-select"
                    >
                        <option value="7days">Last 7 days</option>
                        <option value="30days">Last 30 days</option>
                        <option value="90days">Last 90 days</option>
                        <option value="1year">Last year</option>
                    </select>
                    <button className="export-btn">Export</button>
                </div>
            </div>

            {/* Creators Section */}
            <div className="dashboard-section">
                <div className="section-header">
                    <h2>Creators</h2>
                </div>
                
                <div className="creators-kpis-grid">
                    {(() => {
                        const totalCreations = extractValueAndTrend(creatorsData.totalCreations);
                        const trendData = totalCreations.trendData ? extractTrendData(totalCreations.trendData) : undefined;
                        return (
                            <KPICard
                                title="Total Creations"
                                value={totalCreations.value}
                                trend={totalCreations.trend}
                                trendLabel={totalCreations.trendLabel}
                                trendData={trendData}
                                absoluteChange={totalCreations.absoluteChange}
                                color={totalCreations.trend && totalCreations.trend >= 0 ? "#4caf50" : "#f44336"}
                                loading={loading}
                            />
                        );
                    })()}
                    {(() => {
                        const arpc = extractValueAndTrend(creatorsData.arpc);
                        const trendData = arpc.trendData ? extractTrendData(arpc.trendData) : undefined;
                        return (
                            <KPICard
                                title="ARPC"
                                subtitle="Avg. Revenue Per Creator"
                                value={formatCurrency(arpc.value)}
                                trend={arpc.trend}
                                trendLabel={arpc.trendLabel}
                                trendData={trendData}
                                absoluteChange={arpc.absoluteChange}
                                formatAbsoluteChange={formatCurrency}
                                color={arpc.trend && arpc.trend >= 0 ? "#4caf50" : "#f44336"}
                                loading={loading}
                            />
                        );
                    })()}
                    {(() => {
                        const avgGrnv = extractValueAndTrend(creatorsData.avgGrnvPerCreator);
                        const trendData = avgGrnv.trendData ? extractTrendData(avgGrnv.trendData) : undefined;
                        return (
                            <KPICard
                                title="Avg. GRNV per Creator"
                                value={formatCurrency(avgGrnv.value)}
                                trend={avgGrnv.trend}
                                trendLabel={avgGrnv.trendLabel}
                                trendData={trendData}
                                absoluteChange={avgGrnv.absoluteChange}
                                formatAbsoluteChange={formatCurrency}
                                color={avgGrnv.trend && avgGrnv.trend >= 0 ? "#4caf50" : "#f44336"}
                                loading={loading}
                            />
                        );
                    })()}
                    {(() => {
                        // Check both possible field names from API
                        const rateValue = creatorsData.creationsOnUsersRate !== undefined 
                            ? creatorsData.creationsOnUsersRate 
                            : creatorsData.creatorsOnUsersRate;
                        const creationsRate = extractValueAndTrend(rateValue);
                        const trendData = creationsRate.trendData ? extractTrendData(creationsRate.trendData) : undefined;
                        return (
                            <KPICard
                                title="Creators on users rate"
                                value={formatPercentage(creationsRate.value)}
                                trend={creationsRate.trend}
                                trendLabel={creationsRate.trendLabel}
                                trendData={trendData}
                                absoluteChange={creationsRate.absoluteChange}
                                color={creationsRate.trend && creationsRate.trend >= 0 ? "#4caf50" : "#f44336"}
                                loading={loading}
                            />
                        );
                    })()}
                    <KPICard
                        title="Avg. Time to first creation"
                        value={`${creatorsData.avgTimeToFirstCreation || 0} day${creatorsData.avgTimeToFirstCreation > 1 ? 's' : ''}`}
                        color="#4caf50"
                        loading={loading}
                    />
                    <KPICard
                        title="Creation frequency"
                        value={`${creatorsData.creationFrequency || 0} days`}
                        color="#f44336"
                        loading={loading}
                    />
                    <KPICard
                        title="Avg. Time to first earning"
                        value={`${creatorsData.avgTimeToFirstEarning || 0} days`}
                        color="#f44336"
                        loading={loading}
                    />
                    <KPICard
                        title="Offer VS Demand"
                        value={formatPercentage(creatorsData.offerVsDemand || 0)}
                        color="#f44336"
                        loading={loading}
                    />
                </div>

                <div className="creators-charts">
                    <div className="chart-card">
                        <div className="chart-kpi">
                            <span className="kpi-label">Creators (Paid vs Free Creations)</span>
                            <span className="kpi-value">{creatorsData.creatorsPaidVsFreePercentage}%</span>
                            <span className="kpi-trend positive">+1% since last week</span>
                        </div>
                        <StackedBarChart
                            data={creatorsData.creatorsPaidVsFree}
                            xAxisKey="date"
                            bars={[
                                { key: 'Paid', color: '#ec407a', name: 'Creators with paid creations' },
                                { key: 'Free', color: '#42a5f5', name: 'Creators with free creations only' }
                            ]}
                            height={300}
                            formatTooltip={(value) => `${value}%`}
                            loading={loading}
                        />
                    </div>

                    <div className="chart-card" style={{ position: 'relative' }}>
                        <div className="not-implemented-overlay">
                            <div className="not-implemented-message">
                                <span className="not-implemented-icon">🚧</span>
                                <h3>Not Implemented Yet</h3>
                                <p>This section is currently using dummy data. Implementation coming soon.</p>
                            </div>
                        </div>
                        <div className="chart-kpi">
                            <span className="kpi-label">Creators per tier</span>
                        </div>
                        <PieChart
                            data={creatorsData.creatorsPerTier}
                            dataKey="value"
                            nameKey="name"
                            height={300}
                            colors={['#42a5f5', '#ec407a', '#1976d2', '#f44336']}
                            loading={loading}
                        />
                    </div>
                </div>
            </div>

            {/* Conversion and Activation Section */}
            <div className="dashboard-section conversion-section">
                <div className="section-header">
                    <h2>Conversion and Activation</h2>
                </div>
                <div className="not-implemented-overlay">
                    <div className="not-implemented-message">
                        <span className="not-implemented-icon">🚧</span>
                        <h3>Not Implemented Yet</h3>
                        <p>This section is currently using dummy data. Implementation coming soon.</p>
                    </div>
                </div>

                <div className="chart-card">
                    <div className="chart-kpi">
                        <span className="kpi-label">Creators Conversion per tier</span>
                    </div>
                    <GroupedBarChart
                        data={creatorsData.conversionPerTier}
                        xAxisKey="tier"
                        bars={[
                            { key: 'Sign-ups', color: '#1976d2', name: 'Sign-ups' },
                            { key: 'Profiles completed', color: '#42a5f5', name: 'Profiles completed' },
                            { key: 'First upload', color: '#ec407a', name: 'First upload' },
                            { key: 'First sales', color: '#f44336', name: 'First sales' }
                        ]}
                        height={300}
                        loading={loading}
                    />
                </div>

                <div className="conversion-sales-grid">
                    <KPICard
                        title="Total sales for Super A tier"
                        value={creatorsData.totalSalesSuperA}
                        trend={14}
                        trendLabel="since last week"
                        color="#4caf50"
                        loading={loading}
                    />
                    <KPICard
                        title="Total sales for A tier"
                        value={creatorsData.totalSalesA}
                        trend={-3.9}
                        trendLabel="since last week"
                        color="#f44336"
                        loading={loading}
                    />
                    <KPICard
                        title="Total sales for B tier"
                        value={creatorsData.totalSalesB}
                        trend={-4.0}
                        trendLabel="since last week"
                        color="#f44336"
                        loading={loading}
                    />
                    <KPICard
                        title="Total sales for C tier"
                        value={creatorsData.totalSalesC}
                        trend={-5.1}
                        trendLabel="since last week"
                        color="#f44336"
                        loading={loading}
                    />
                </div>

                <div className="chart-card">
                    <div className="chart-kpi">
                        <span className="kpi-label">Creators Conversion Funnel</span>
                    </div>
                    <FunnelChart
                        data={creatorsData.conversionFunnel}
                        height={400}
                        loading={loading}
                    />
                </div>
            </div>

            {/* Creations and News Section */}
            <div className="dashboard-section">
                <div className="section-header">
                    <h2>Creations and News</h2>
                </div>

                <div className="creations-news-kpis-grid">
                    {(() => {
                        const totalCreations = extractValueAndTrend(creatorsData.totalCreations);
                        const trendData = totalCreations.trendData ? extractTrendData(totalCreations.trendData) : undefined;
                        return (
                            <KPICard
                                title="Total Creations"
                                value={totalCreations.value}
                                trend={totalCreations.trend}
                                trendLabel={totalCreations.trendLabel}
                                trendData={trendData}
                                absoluteChange={totalCreations.absoluteChange}
                                color={totalCreations.trend && totalCreations.trend >= 0 ? "#4caf50" : "#f44336"}
                                loading={loading}
                            />
                        );
                    })()}
                    {(() => {
                        const avgCreations = extractValueAndTrend(creatorsData.avgCreationsPerCreator);
                        const trendData = avgCreations.trendData ? extractTrendData(avgCreations.trendData) : undefined;
                        return (
                            <KPICard
                                title="Avg. Creations per creator"
                                value={avgCreations.value}
                                trend={avgCreations.trend}
                                trendLabel={avgCreations.trendLabel}
                                trendData={trendData}
                                absoluteChange={avgCreations.absoluteChange}
                                color={avgCreations.trend && avgCreations.trend >= 0 ? "#4caf50" : "#f44336"}
                                loading={loading}
                            />
                        );
                    })()}
                    {(() => {
                        const newCreations = extractValueAndTrend(creatorsData.newCreations);
                        const trendData = newCreations.trendData ? extractTrendData(newCreations.trendData) : undefined;
                        return (
                            <KPICard
                                title="New Creations"
                                value={newCreations.value}
                                trend={newCreations.trend}
                                trendLabel={newCreations.trendLabel}
                                trendData={trendData}
                                absoluteChange={newCreations.absoluteChange}
                                color={newCreations.trend && newCreations.trend >= 0 ? "#4caf50" : "#f44336"}
                                loading={loading}
                            />
                        );
                    })()}
                    {(() => {
                        const news = extractValueAndTrend(creatorsData.news);
                        const trendData = news.trendData ? extractTrendData(news.trendData) : undefined;
                        return (
                            <KPICard
                                title="News"
                                value={news.value}
                                trend={news.trend}
                                trendLabel={news.trendLabel}
                                trendData={trendData}
                                absoluteChange={news.absoluteChange}
                                color={news.trend && news.trend >= 0 ? "#4caf50" : "#f44336"}
                                loading={loading}
                            />
                        );
                    })()}
                    <KPICard
                        title="Total News"
                        value={creatorsData.totalNews || 0}
                        color="#f44336"
                        loading={loading}
                    />
                    <KPICard
                        title="Avg. News per creator"
                        value={creatorsData.avgNewsPerCreator || 0}
                        color="#f44336"
                        loading={loading}
                    />
                </div>

                <div className="chart-card">
                    <div className="chart-kpi">
                        <span className="kpi-label">New creations and News</span>
                    </div>
                    <LineChart
                        data={creatorsData.newCreationsAndNewsTrend}
                        xAxisKey="date"
                        lines={[
                            { key: 'New Creations', color: '#ec407a', name: 'New Creations' },
                            { key: 'News', color: '#f44336', name: 'News' }
                        ]}
                        height={300}
                        loading={loading}
                    />
                </div>

                <div className="creations-charts-row">
                    <div className="chart-card">
                        <div className="chart-kpi">
                            <span className="kpi-label">Creations per cultural area</span>
                            <span className="kpi-value">{extractValueAndTrend(creatorsData.totalCreations).value}</span>
                        </div>
                        <BarChart
                            data={creatorsData.creationsPerCulturalArea}
                            xAxisKey="name"
                            dataKey="value"
                            color="#ec407a"
                            height={300}
                            loading={loading}
                        />
                    </div>

                    <div className="chart-card">
                        <div className="chart-kpi">
                            <span className="kpi-label">Creations per category</span>
                            <span className="kpi-value">{extractValueAndTrend(creatorsData.totalCreations).value}</span>
                        </div>
                        <BarChart
                            data={creatorsData.creationsPerCategory}
                            xAxisKey="name"
                            dataKey="value"
                            color="#ec407a"
                            height={300}
                            loading={loading}
                        />
                    </div>
                </div>

                <div className="creations-charts-row">
                    <div className="chart-card">
                        <div className="chart-kpi">
                            <span className="kpi-label">Paid creations vs Free creations</span>
                        </div>
                        <PieChart
                            data={creatorsData.paidVsFreeCreations}
                            dataKey="value"
                            nameKey="name"
                            height={300}
                            colors={['#42a5f5', '#ec407a']}
                            loading={loading}
                        />
                    </div>

                    <div className="chart-card">
                        <div className="chart-kpi">
                            <span className="kpi-label">Offer vs Demand</span>
                        </div>
                        <div className="offer-demand-kpis">
                            {(() => {
                                const paidCreations = extractValueAndTrend(creatorsData.paidCreations);
                                const trendData = paidCreations.trendData ? extractTrendData(paidCreations.trendData) : undefined;
                                return (
                                    <KPICard
                                        title="Paid creations"
                                        value={paidCreations.value}
                                        trend={paidCreations.trend}
                                        trendLabel={paidCreations.trendLabel}
                                        trendData={trendData}
                                        absoluteChange={paidCreations.absoluteChange}
                                        color={paidCreations.trend && paidCreations.trend >= 0 ? "#4caf50" : "#f44336"}
                                        loading={loading}
                                    />
                                );
                            })()}
                            {(() => {
                                const newOffers = extractValueAndTrend(creatorsData.newCreatedOffers);
                                const trendData = newOffers.trendData ? extractTrendData(newOffers.trendData) : undefined;
                                return (
                                    <KPICard
                                        title="Monetized Creations "
                                        value={newOffers.value}
                                        trend={newOffers.trend}
                                        trendLabel={newOffers.trendLabel}
                                        trendData={trendData}
                                        absoluteChange={newOffers.absoluteChange}
                                        color={newOffers.trend && newOffers.trend >= 0 ? "#4caf50" : "#f44336"}
                                        loading={loading}
                                    />
                                );
                            })()}
                        </div>
                        <LineChart
                            data={creatorsData.offerVsDemandTrend}
                            xAxisKey="date"
                            lines={[
                                { key: 'Paid creations', color: '#4caf50', name: 'Paid creations' },
                                { key: 'New created offers', color: '#ec407a', name: 'Monetized Creations ' }
                            ]}
                            height={300}
                            loading={loading}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Creators;
